import { UserJSON } from "../public/js/models/users";
import { EventJSON } from "../public/js/models/events";
import { CompoJSON, CompoType } from "../public/js/models/compos";
import { EntryJSON } from "../public/js/models/entries";
import { CommentJSON, CommentType } from "../public/js/models/comments";
import { VoteJSON } from "../public/js/models/vote";
import { TagJSON } from "../public/js/models/tags";
import { EntryFinalResults } from "../public/js/models/entryfinalresults";
import { NotificationJSON } from "../public/js/models/notifications";
import { ThemeVoteType } from "../public/js/Compo/ThemeSuggestPage";
import { TrophyJSON } from "../public/js/models/trophies";
import type { EntryType } from "../public/js/Compo/CompoPage";
import { Feed } from "../server/feed/buildfeed";
import { VotingResults, AdditionalResults } from "../server/results";
import { ReviewFavoritesJSON } from "../public/js/models/review_favorites";
import { ExperienceTypes } from "../server/exp_boost";
import { ForumTopicJSON } from "../public/js/models/forum_topics";
import { ForumTopicResponse } from "../server/routes/forums";
import { CompoCategoriesJSON } from "./entity/CompoCategoriesClient";
import { CompoCategories } from "./entity/CompoCategories";
import type { ModerationActionType } from "./moderation";
import { ModChecklistJSON } from "../public/js/models/modchecklist";

export type Toast<T = undefined> = {
  type: "error" | "success";
  redirect?: string;
  refresh?: boolean;
  goldDiff?: number;
  push?: boolean;
  data?: T;
  msg: string;
};

// Old-style errors
export type API_Error = { error: 1; msg: string };

export type ChipsAPI = {
  "/api/user": {
    GET: {
      response: UserJSON | undefined;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/admin/tasks": {
    GET: {
      response: (ModChecklistJSON & { username: string })[] | Toast;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/cacao/participants": {
    GET: {
      response: {
        amount: number;
        username: string;
        avatar_url: string;
        changes: number[];
        reason: string[];
      }[];
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/login": {
    POST: {
      response: Toast;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/forgot-password": {
    POST: {
      response: Toast;
      params: {};
      query: {};
      body: { email: string };
    };
  };

  "/api/reset-password": {
    POST: {
      response: Toast;
      params: {};
      query: {};
      body: { token: string; password: string };
    };
  };

  "/api/reset-password/validate": {
    POST: {
      response: { valid: boolean } | Toast;
      params: {};
      query: {};
      body: { token: string };
    };
  };

  "/api/user/:username": {
    GET: {
      response: API_UserId | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/user/:username/plays": {
    GET: {
      response:
        | {
            entry_id: number;
            count: number;
            title: string;
          }[]
        | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/user/:username/plays/stats": {
    GET: {
      response:
        | {
            totalListens: number;
            uniqueSongs: number;
            userCreated: string;
            topAuthors: {
              userId: number;
              username: string;
              avatarUrl: string;
              playCount: number;
            }[];
          }
        | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/user/:username/favorites/stats": {
    GET: {
      response:
        | {
            totalFavorites: number;
            userCreated: string;
            topAuthors: {
              userId: number;
              username: string;
              avatarUrl: string;
              favoriteCount: number;
            }[];
          }
        | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/user/:username/comments": {
    GET: {
      response: (CommentJSON & { entry_title?: string })[] | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/user/:username/reviews": {
    GET: {
      response: (VoteJSON & { entry_title?: string })[] | Toast;
      params: { username: string };
      query: {};
      body: {};
    };
  };

  "/api/notifications": {
    GET: {
      response: API_UserNotifications;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/notifications/unread": {
    GET: {
      response: number;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/results": {
    GET: {
      response: VotingResults | Toast;
      params: {
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/results/secret_endpoint_dont_tell_anyone": {
    GET: {
      response: VotingResults | Toast;
      params: {
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/additional-results": {
    GET: {
      response: AdditionalResults;
      params: {
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/hide": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/admin/tasks/:id": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/review/:id/favorite": {
    POST: {
      response: ReviewFavoritesJSON | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/judge/:id/delete": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: { reason?: string };
    };
  };

  "/api/compo/judge/:id/moderation-log": {
    GET: {
      response:
        | {
            events: {
              id: string;
              createdAt: string;
              actorUsername: string;
              actionType: string;
              reason: string | null;
            }[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/favorite": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
    DELETE: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/download": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/play": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/user/profile": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        send_email: "on" | "off";
        send_notification_emails: "on" | "off";
        profile: string;
        play_visibility: string;
        file?: File;
      };
    };
  };

  "/api/compo/vote/:id/hide": {
    POST: {
      response: {} | Toast;
      params: {
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/compo/:compo_id/judge/:entry_id": {
    POST: {
      response: Toast<VoteJSON>;
      params: {
        compo_id: string;
        entry_id: string;
      };
      query: {};
      body: {
        category_0: number;
        category_1: number;
        category_2: number;
        category_3: number;
        category_4: number;
        category_5: number;
        category_6: number;
        category_7: number;
        category_8: number;

        public: boolean;

        review?: string;
        guess?: string;
      };
    };
  };

  "/api/compo/:id/admin/votesrequired": {
    GET: {
      response: VotesRequiredAPI | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/forum/subscription/:topic_id": {
    DELETE: {
      response: Toast;
      params: { topic_id: string };
      query: {};
      body: {};
    };

    POST: {
      response: Toast;
      params: { topic_id: string };
      query: {};
      body: {};
    };
  };

  "/api/forum": {
    POST: {
      response: Toast<ForumTopicJSON>;
      params: {};
      query: {};
      body: {
        title: string;
        body: string;
        board: string;
        subscribe: boolean;
      };
    };

    GET: {
      response: Toast<ForumTopicResponse[]>;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/forum/topic/:id": {
    GET: {
      response: Toast<ForumTopicResponse>;
      params: { id: string };
      query: {};
      body: {};
    };

    PUT: {
      response: Toast<ForumTopicJSON>;
      params: { id: string };
      query: {};
      body: {
        title: string;
        body: string;
        board: string;
      };
    };
  };

  "/api/notifications/read": {
    GET: {
      response: NotificationJSON[] | Toast;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/admin/givexp": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        username: string;
        type: ExperienceTypes;
      };
    };
  };

  "/api/admin/givegold": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        silver: number;
        username: string;
      };
    };
  };

  "/api/admin/givecacao": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        cacao: number;
        reason: string;
        username: string;
      };
    };
  };

  "/api/admin/ban": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        user_id: number;
        reason: string;
      };
    };
  };

  "/api/admin/unban": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        user_id: number;
        reason: string;
      };
    };
  };

  "/api/admin/user/:id/moderation-history": {
    GET: {
      response:
        | {
            events: {
              id: string;
              createdAt: string;
              actionType: ModerationActionType;
              actorUsername: string;
              reason: string | null;
              content: string | null;
            }[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/admin/save": {
    POST: {
      response: {} | Toast;
      params: {
        id: string;
      };
      query: {};
      body: {
        type: "main" | "day" | "hour" | "meme";
        name: string;
        primary_image: string;
        theme_short: string;
        description: string;
        theme: string;
        nuclearOption: boolean;
        allow_voting: boolean;
        header_image: string;
        types: string;
        nogold: boolean;
        start: string;
        stop: string;
        judging_stop: string;
        hide_images_before_compo_start: boolean;
        is_secret_santa: boolean;
      };
    };
  };

  "/api/compo/:id/commit": {
    GET: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/admin/voteaudit": {
    GET: {
      response:
        | {
            votes: VoteJSON[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:compo_id/admin/user/:user_id/ignore-all-votes": {
    POST: {
      response: {} | Toast;
      params: { compo_id: string; user_id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:compo_id/admin/user/:user_id/vote-moderation-log": {
    GET: {
      response:
        | {
            events: {
              id: string;
              createdAt: string;
              actorUsername: string;
              actionType: string;
              reason: string | null;
              voteCount?: number;
            }[];
          }
        | Toast;
      params: { compo_id: string; user_id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/admin/blast-trophies": {
    GET: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/open-compos": {
    GET: {
      response: API_OpenCompos;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/search/:search": {
    GET: {
      response: API_Search;
      params: { search: string };
      query: { types?: string }; // comma-separated: "entries", "users", "compos" (default: all)
      body: {};
    };
  };

  "/api/entries/top": {
    GET: {
      response: API_TopEntries;
      params: { search: string };
      query: {
        entryType: EntryType;
        sortByLikes: string;
        compoType: CompoType | "all";
        category: string;
        username: string;
        inTheLast: string;
      };
      body: {};
    };
  };

  "/api/sign-s3": {
    GET: {
      response:
        | {
            signedRequest: string;
            url: string;
          }
        | Toast;
      params: {};
      query: {
        "file-name": string;
        "file-type": string;
      };
      body: {};
    };
  };

  "/api/compo": {
    GET: {
      response: API_Compo;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/comment": {
    POST: {
      response: CommentJSON | Toast;
      params: { id: string };
      query: {};
      body: {
        anonymous: boolean;
        content: string;
        type: CommentType;
        subscribe: boolean;
      };
    };
  };

  "/api/edit/comment/:id": {
    POST: {
      response: CommentJSON | Toast;
      params: { id: string };
      query: {};
      body: {
        content: string;
      };
    };
  };

  "/api/edit/review/:id": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {
        content: string;
      };
    };
  };

  "/api/event/create": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        title: string;
        description: string;
        start: string;
        url: string;
        image: string;
      };
    };
  };

  "/api/event/:id/edit": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {
        title: string;
        description: string;
        start: string;
        url: string;
        image: string;
      };
    };
  };

  "/api/user/:id/trophies": {
    GET: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/admin/create": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {
        judging_stop: string;
        name: string;
        allow_voting: boolean;
        primary_image: string;
        header_image: string;
        start: string;
        stop: string;
        theme: string;
        theme_short: string;
        hide_images_before_compo_start: boolean;
      };
    };
  };

  "/api/saying": {
    GET: {
      response: { msg: string }[];
      params: { id: string };
      query: {};
      body: {};
    };

    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {
        msg: string;
      };
    };
  };

  "/api/compo/:id/admin/save-categories": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {
        type: EntryType;
        category_1: string;
        category_2: string;
        category_3: string;
        category_4: string;
        category_5: string;
        category_6: string;
        category_7: string;
        category_8: string;
        category_9: string;
      };
    };
  };

  "/api/compo/:id/finalize-xp/:divider": {
    GET: {
      response: {} | Toast;
      params: {
        divider: string;
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/finalize-gold": {
    GET: {
      response: {} | Toast;
      params: {
        id: string;
      };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/admin/gen_votes_required": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
    GET: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/admin/gen_votes_required/status": {
    GET: {
      response:
        | {
            hasExistingVotes: boolean;
            uniqueVoters: number;
            totalVotes: number;
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/admin/compo/:id/badvoters": {
    GET: {
      response: BadVotersAPI | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/comment/:comment_id/like": {
    POST: {
      response: Toast;
      params: { comment_id: string };
      query: {};
      body: {
        anonymous: boolean;
        content: string;
        type: CommentType;
      };
    };
  };

  "/api/entry/:entry_id/tag": {
    POST: {
      response: TagJSON | Toast;
      params: { entry_id: string };
      query: {};
      body: {
        tag: string;
      };
    };
  };

  "/api/tag/:tag": {
    GET: {
      response: API_TagTag;
      params: { tag: string };
      query: {};
      body: {};
    };
  };

  "/api/tag/:tag_id/delete": {
    PUT: {
      response: {} | Toast;
      params: { tag_id: number };
      query: {};
      body: {};
    };
  };

  "/api/comment/:id/delete": {
    POST: {
      response: {} | Toast;
      params: { id: string };
      query: {};
      body: { reason?: string };
    };
  };

  "/api/comment/:id/moderation-log": {
    GET: {
      response:
        | {
            events: {
              id: string;
              createdAt: string;
              actorUsername: string;
              actionType: string;
              reason: string | null;
            }[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/collab/:entry_id/:status": {
    POST: {
      response: {} | Toast;
      params: {
        entry_id: string;
        status: "confirm" | "reject";
      };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id/edit": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {
        title: string;
        description: string;
        uploaded_url: string;
        downloadable: "on" | "off";
        followed_theme: boolean;
        is_judged: boolean;
        is_joke: boolean;

        collaborator_0?: string;
        collaborator_1?: string;
        collaborator_2?: string;
        collaborator_3?: string;
        collaborator_4?: string;
        collaborator_5?: string;
        mod_comments?: string;
        override_deadline?: boolean;
        secret_santa_recipient?: string;
        secret_santa_prompt_text?: string;
      };
    };
  };

  "/api/compo/:id/judge": {
    GET: {
      response:
        | {
            judgingProgress: {
              entry: EntryJSON;
              voteRequired: boolean;
              vote: VoteJSON;
              reviewBounty: number;
            }[];
            compo: CompoJSON;
            categories: CompoCategories[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:compo_id/submit": {
    POST: {
      response: Toast<EntryJSON>;
      params: { compo_id: string };
      query: {};
      body: {
        title: string;
        description: string;
        "uploaded-file-url": string;
        tags: string;
        type: EntryType;
        followed_theme: boolean;
        isJudged: boolean;
        isJoke: boolean;
        downloadable: boolean;
        collaborator_0?: string;
        collaborator_1?: string;
        collaborator_2?: string;
        collaborator_3?: string;
        collaborator_4?: string;
        collaborator_5?: string;
        secret_santa_recipient?: string;
        secret_santa_prompt_text?: string;
      };
    };
  };

  "/api/register": {
    POST: {
      response: {} | Toast;
      params: {};
      query: {};
      body: {
        username: string;
        password: string;
        email: string;
        send_email: boolean;
        invited_by: string;
      };
    };
  };

  "/api/entry/:id/favorites": {
    GET: {
      response: { users: UserJSON[] };
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/guesstable": {
    GET: {
      response: API_GuessTable | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/entries": {
    GET: {
      response: EntryJSON[] | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/entry/:id": {
    GET: {
      response: API_EntryId | Toast;
      params: { id: string };
      query: {
        deanon?: boolean;
      };
      body: {};
    };
  };

  "/api/entry/:id/stats": {
    GET: {
      response: API_EntryId | Toast;
      params: { id: string };
      query: {
        deanon?: boolean;
      };
      body: {};
    };
  };

  "/api/random": {
    GET: {
      response: { entry_id: number | null };
      params: {};
      query: { likes?: string; type?: string };
      body: {};
    };
  };

  "/api/registry": {
    GET: {
      response: API_Registry;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/most-intelligent": {
    GET: {
      response: {
        users: {
          userId: number;
          username: string;
          avatarUrl: string;
          fleschKincaid: number;
          gradeDescription: string;
          totalWords: number;
          totalComments: number;
          totalReviews: number;
        }[];
      };
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/compo/:id": {
    GET: {
      response: API_CompoId | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/index": {
    GET: {
      response: Feed;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/feed": {
    GET: {
      response: Feed;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/logout": {
    GET: {
      response: Toast;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/mod-feed": {
    GET: {
      response: API_ModFeed | Toast;
      params: {};
      query: {};
      body: {};
    };
  };

  "/api/event/:id": {
    GET: {
      response: API_EventId;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/theme": {
    POST: {
      response: Toast;
      params: {};
      query: {};
      body: {
        theme: string;
        compo_id: number;
        uploaded_url?: string;
      };
    };
  };

  "/api/theme/:id": {
    GET: {
      response: API_Theme | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/vote/theme": {
    GET: {
      response: API_Vote | Toast;
      params: {};
      query: {};
      body: {};
    };

    POST: {
      response: Toast;
      params: {};
      query: {};
      body: {
        vote: 1 | 0 | -1;
        theme_id: number;
      };
    };
  };

  "/api/vote/voters/:id": {
    GET: {
      response: API_Voters;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/vote/delete/:id": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {
        username: string;
      };
    };
  };

  "/api/theme/delete": {
    POST: {
      response: Toast;
      params: {};
      query: {};
      body: {
        banned: boolean;
        theme: string;
      };
    };
  };

  "/api/compo/:id/sync-listen": {
    GET: {
      response:
        | {
            syncListen: SyncListenJSON | null;
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
    POST: {
      response:
        | {
            syncListen: SyncListenJSON;
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync-listen/entries": {
    GET: {
      response:
        | {
            entries: SyncListenEntryJSON[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {
        entryIds: number[];
      };
    };
  };

  "/api/compo/:id/sync": {
    GET: {
      response:
        | {
            compo: CompoJSON;
            syncListen: SyncListenJSON | null;
            entries: SyncListenPublicEntryJSON[];
          }
        | Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/start": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/stop": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/delete": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/next": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/prev": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/set-discord-sync": {
    POST: {
      response: Toast;
      params: { id: string };
      query: {};
      body: {};
    };
  };

  "/api/compo/:id/sync/chat": {
    GET: {
      response:
        | {
            messages: SyncListenChatMessageJSON[];
          }
        | Toast;
      params: { id: string };
      query: { before?: string }; // cursor for pagination
      body: {};
    };
  };

  "/api/compo/:id/sync/chat/send": {
    POST: {
      response:
        | {
            message: SyncListenChatMessageJSON;
          }
        | Toast;
      params: { id: string };
      query: {};
      body: { content: string };
    };
  };
};

export type SyncListenState = "paused" | "playing" | "gap_between_entries";

export type SyncListenJSON = {
  id: number;
  compoId: number;
  createdAt: string;
  state: SyncListenState;
  currentEntryIndex: number;
  currentEntrySeconds: number;
  gapSeconds: number;
  lastTickAt: string | null;
  syncedToDiscordChannel: boolean;
};

export type SyncListenEntryJSON = {
  id: number;
  syncListenId: number;
  entryId: number;
  position: number;
};

export type SyncListenPublicEntryJSON = {
  id: number;
  entryId: number;
  position: number;
  title: string;
  uploadedUrl: string;
  durationSeconds: number | null;
};

export type SyncListenChatMessageJSON = {
  id: number;
  syncListenId: number;
  messageType: "user" | "discord" | "server";
  // For user messages
  userId: number | null;
  username: string | null;
  avatarUrl: string | null;
  // For discord messages
  discordUserId: string | null;
  discordUsername: string | null;
  // For server messages
  serverMessageType: string | null;
  // Message content
  content: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export type Review = UserJSON & {
  review: string;
  created: Date;
  entry_id: number;
  entry_title: string;
};

export type BadVotersAPI = {
  user_id: number;
  compo_id: number;
  count: number;
  email: string;
  username: string;
}[];

export type EntryReviewUser = {
  id: number;
  username: string;
  avatar_url: string;
  judge: boolean;
};

export type EntryReview = {
  review: string;
  review_created: Date;
  vote_id: number;
  review_favorites: number;
  has_favorited: boolean;
  hidden: boolean;
  author_banned: boolean;
} & EntryReviewUser;

export type API_EntryId = {
  entry: EntryJSON;
  entry_users: UserJSON[];
  unofficial_entry_users: UserJSON[];
  compo: CompoJSON;
  categories: CompoCategoriesJSON[];
  public_votes: (VoteJSON & { username: string; avatar_url: string })[];
  judge_votes: VoteJSON[];
  reviews: EntryReview[];
  tags: TagJSON[];
  entryCounts: undefined | { [key in EntryType]: number };

  comments: {
    comment: CommentJSON;
    user: UserJSON;
    likes: number;
    is_liked: boolean;
    author_banned: boolean;
  }[];

  favorite_count: number;
  final_results: EntryFinalResults | undefined;
  vote: VoteJSON | undefined;
  has_favorited: boolean;
  is_author: boolean;
  secret_santa_recipient: UserJSON | undefined;
  secret_santa_recipient_external_user_name: string | undefined;
};

export type VotesRequiredAPI = {
  user: UserJSON;
  users: UserJSON[];
  aggOnUser: { [key: number]: number[] };
  aggOnEntry: { [key: number]: number[] };
};

export type API_EventId = EventJSON;

export type API_Theme = {
  id: number;
  theme: string;
  theme_pic: string | null;
  score: number;
}[];

export type API_Vote = { vote: ThemeVoteType; theme_id: number }[];

export type API_Voters = { username: string; count: number }[];

export type Message = {
  message_text: string;
  recipient_user_id: number;
  sender_user_id: number;
  recipient_seen: boolean;
  timestamp: Date;
};

export type API_Messages = Message[];

export type API_Registry = {
  users: {
    user: UserJSON;
    main_entries: number;
    side_entries: number;
  }[];
};

export type API_Messages_Obj = {
  messages: {
    [user_id: number]: {
      messages: {
        sender_user_id: number;
        message_text: string;
        timestamp: Date;
      }[];
      seen: boolean;
    };
  };
  unread_count: number;
};

export type API_UsersYouAreMessaging = {
  id: number;
  username: string;
  avatar_url: string;
}[];

export type EntryCompoJSON = EntryJSON & {
  compo_id: number;
  compo_name: string;
  compo_type: string;
  released: boolean;
};

export type ExpandedEntryJSON = EntryJSON & {
  avatar_url_1: string;
  avatar_url_2: string;
  avatar_url_3: string;
  avatar_url_4: string;
  avatar_url_5: string;

  username_1: string;
  username_2: string;
  username_3: string;
  username_4: string;
  username_5: string;
};

export function IsExpandedEntryJSON(x: any): x is ExpandedEntryJSON {
  return !!(
    x.username_1 ||
    x.username_2 ||
    x.username_3 ||
    x.username_4 ||
    x.username_5
  );
}

export type API_Compo = {
  compo: CompoJSON;
  entries: ExpandedEntryJSON[];
  entryCount: number;
}[];

export type API_UserId = {
  entries: EntryJSON[];
  associatedCompos: {
    compo_id: number;
    name: string;
    type: CompoType;
  }[];
  avatarUrls: string[][];
  favorites: EntryCompoJSON[];
  user: UserJSON;
  entryResults: EntryFinalResults[];
  trophies: TrophyJSON[];

  comments: {
    comment: CommentJSON;
    user: UserJSON;
    likes: number;
    is_liked: boolean;
    author_banned: boolean;
  }[];

  counts: {
    comments: number;
    reviews: number;
    plays: number;
    favorites: number;
  };
};

export type API_OpenCompos = CompoJSON[];

export interface API_Search {
  entries: EntryJSON[];
  compos: CompoJSON[];
  users: UserMinimal[];
}

export type UserMinimal = {
  username: string;
  avatar_url: string;
};

export type API_ModFeed = (
  | (TagJSON & { username: string; title: string })
  | (CommentJSON & { username: string; title: string })
  | (VoteJSON & { username: string; title: string })
)[];

export type API_UserNotifications = {
  notifications: API_Notification[];

  unread_count: number;
};

export type API_Notification = {
  notification: NotificationJSON;
  content: string;
  url: string;

  comment?: CommentJSON;
  review?: string;
  entry?: EntryJSON;
  user?: UserJSON;
  topic?: ForumTopicJSON;
};

export type API_TagTag = {
  entries: (EntryJSON & {
    compo_id: number;
    name: string;
    compo_type: CompoType;
  })[];
};

export type API_CompoId = {
  compo: CompoJSON;
  images: any[]; // sigh
  entries: EntryJSON[];
  votesPerEntry: number;
  favoriteEntryIDs: number[];
  commentedEntryIDs: number[];
  entriesSubmitted: { [key in EntryType]: number };
  can_judge: boolean;
  categories: CompoCategoriesJSON[];
  avatarUrls: string[][];
};

export type API_TopEntries = EntryJSON[];

export type API_GuessTable = {
  table: {
    [username: string]: {
      entry: string;
      username: string;
      correct: boolean;
    }[];
  };

  entries: { id: number; title: string }[];
  users: string[];
};

export function isAPIError(object: any): object is API_Error {
  return object.error === 1;
}

export type APIIdPlaysResponse = {
  entry_id: number;
  count: number;
  title: string;
}[];
